import classNames from 'classnames'
import React, { useEffect } from 'react'

import { ClearAllFilters } from '../clear-all-filters'
import ShowDealsButton from '../show-deals-button'
import Sort from '../sort'
import { Accordion, AccordionItem } from '~/components/accordion'
import CloseButton from '~/components/close-button'
import skeletonStyles from '~/components/skeletons/skeleton-styles.module.css'
import { usePageContext } from '~/contexts/page-context'
import { useInBucket } from '~/hooks/use-in-bucket'
import smoothScrollTo from '~/utils/smooth-scroll-to'
import { track } from '~/utils/tracking'

import styles from './styles.module.css'

const TABLET_BREAKPOINT = 992
const SCROLL_DURATION = 10
const SCROLL_DELAY = 300

const filterGroupMatchesIntent = (intent, filterGroupIntent) => intent.includes(filterGroupIntent)

export const renderFilterGroup = ({
  filterGroup,
  index,
  filterData,
  isSidebarOpen,
  selectedFilters,
  onChangeFilterGroup = () => {},
  isCityFibreTrial
}) => {
  const {
    FacetType,
    data,
    name,
    sortingFn,
    shouldShowUnit,
    overrideUnit,
    showUnit,
    showLogo,
    displayUnit,
    ranges,
    tooltipTexts,
    formatLabelFn,
    objKey,
    title,
    maxVisibleFacets
  } = filterGroup

  const isLastChild = index === filterData.length - 1

  const showFilterGroup = isSidebarOpen || !filterGroup.intent || filterGroupMatchesIntent(filterGroup.intent)

  if (
    !showFilterGroup ||
    objKey === 'nextGenTrial' ||
    (!isCityFibreTrial && (objKey === 'speedFeatures' || objKey === 'speedCategories')) ||
    (isCityFibreTrial && objKey === 'speed')
  )
    return null

  return (
    <>
      <AccordionItem title={title} key={objKey} index={index} isLastChild={isLastChild}>
        <FacetType
          data={data}
          name={name}
          sortingFn={sortingFn}
          shouldShowUnit={shouldShowUnit}
          overrideUnit={overrideUnit}
          showUnit={showUnit}
          showLogo={showLogo}
          displayUnit={displayUnit}
          ranges={ranges}
          onChangeFilterGroup={onChangeFilterGroup}
          selectedFiltersByFilterGroup={selectedFilters[objKey]}
          tooltipTexts={tooltipTexts}
          formatLabelFn={formatLabelFn}
          facetTitle={title}
          isLastChild={isLastChild}
          maxVisibleFacets={maxVisibleFacets}
        />
      </AccordionItem>
    </>
  )
}

export const runFilterData = ({ availableFilters, filterConfig, isBlackFriday = false, showPriceRiseFilter }) => {
  const shouldAddGroup = (objKey, availableFiltersByObjKey) => {
    if (!availableFiltersByObjKey) return false

    if (objKey === 'specialOffers') return availableFiltersByObjKey.length > 0

    return availableFiltersByObjKey.length > 1
  }

  return filterConfig.reduce((filterList, filter) => {
    const { objKey } = filter

    const availableFiltersByObjKey =
      availableFilters[objKey] &&
      availableFilters[objKey].filter((filterItem) => {
        // Remove the Black Friday filter if the user isn't bucketed into the experiment:
        if (filterItem.value === 'BLACK_FRIDAY' && !isBlackFriday) return false

        // Remove the No Price Rises filter if the user isn't bucketed into the experiment:
        if (filterItem.value === 'NO_PRICE_RISE_2024' && !showPriceRiseFilter) return false

        return true
      })

    return shouldAddGroup(objKey, availableFiltersByObjKey)
      ? [
          ...filterList,
          {
            data: availableFiltersByObjKey,
            ...filter
          }
        ]
      : filterList
  }, [])
}

const Sidebar = ({
  filterConfig,
  availableFilters,
  selectedFilters,
  onChangeFilters,
  order,
  onChangeOrder,
  availableDealsCount,
  intent,
  isSidebarOpen,
  toggleSidebar,
  deals,
  tableAds,
  loading = false,
  setShowBannerButton,
  ...props
}) => {
  const isBlackFriday = useInBucket('experiment-black-friday-2023', 'variant')
  const showPriceRiseFilter = useInBucket('experiment-mid-contract-price-rise-details-badge', 'variant')
  const isCityFibreTest = useInBucket('experiment-broadband-city-fibre-trial', 'variant')

  const { resetSlug, slug } = usePageContext()

  const isCityFibreTrial = slug === 'fibre_results' && isCityFibreTest

  // Manage scrolling when mobiles filter used
  useEffect(() => {
    if (isSidebarOpen && window?.innerWidth < TABLET_BREAKPOINT) {
      setTimeout(() => {
        smoothScrollTo('#table-scroll-target', SCROLL_DURATION)
      }, SCROLL_DELAY)
    }
  }, [isSidebarOpen])

  const changeFilterGroupHandler = (filterGroup) => {
    onChangeFilters({
      ...selectedFilters,
      ...filterGroup
    })
  }

  const closeClickHandler = (e) => {
    track('Broadband - Filter & Sort', 'Modal closed')
    toggleSidebar(e)
  }

  const clearFiltersHandler = (filters) => {
    track('Broadband - Filter & Sort', 'Clear all filters')

    if (resetSlug) resetSlug()
    onChangeFilters(filters)
  }

  const filterData = runFilterData({ availableFilters, filterConfig, isBlackFriday, showPriceRiseFilter })

  return (
    <SidebarTemplate
      {...props}
      filterData={filterData}
      isSidebarOpen={isSidebarOpen}
      toggleSidebar={toggleSidebar}
      filters={filterData.map((filterGroup, index) =>
        renderFilterGroup({
          filterGroup,
          index,
          filterData,
          isSidebarOpen,
          selectedFilters,
          onChangeFilterGroup: (data) => changeFilterGroupHandler({ [filterGroup.objKey]: data }),
          isCityFibreTrial
        })
      )}
      changeFilterGroupHandler={changeFilterGroupHandler}
      closeClickHandler={closeClickHandler}
      clearFiltersHandler={clearFiltersHandler}
      selectedFilters={selectedFilters}
      dealsShown={deals.length + tableAds.length}
      dealsTotal={availableDealsCount + tableAds.length}
      order={order}
      onChangeOrder={onChangeOrder}
      loading={loading}
      availableDealsCount={availableDealsCount}
      setShowBannerButton={setShowBannerButton}
    />
  )
}

export const SidebarTemplate = ({
  filterData,
  filters,
  order,
  isSkeleton = false,
  onChangeOrder = () => {},
  isSidebarOpen = false,
  dealsShown = 0,
  dealsTotal = 0,
  selectedFilters = {},
  filterConfig = [],
  availableDealsCount = 0,
  loading = false,
  toggleSidebar = () => {},
  changeFilterGroupHandler = () => {},
  closeClickHandler = () => {},
  clearFiltersHandler = () => {},
  setShowBannerButton,
  ...rest
}) => {
  if (!filterData.length) return <div {...rest} className={styles.sidebar} data-open={isSidebarOpen} />

  const skeletonTextSelector = isSkeleton ? { 'data-skeleton-text': 'true' } : {}

  return (
    <div
      className={classNames(styles.sidebar, {
        [skeletonStyles.sidebar]: isSkeleton
      })}
      data-open={isSidebarOpen}
      {...rest}
    >
      <div className={styles.header}>
        {dealsTotal > 0 && (
          <div className={styles.dealsCount} {...skeletonTextSelector}>{`${dealsShown} of ${dealsTotal} results`}</div>
        )}

        <ClearAllFilters
          selectedFilters={selectedFilters}
          filterConfig={filterConfig}
          onClearFilters={clearFiltersHandler}
          setShowBannerButton={setShowBannerButton}
        />

        <CloseButton onClick={closeClickHandler} className={styles.closeButton} />
      </div>

      <div className={styles.title} {...skeletonTextSelector}>
        Sort by
      </div>

      <Sort order={order} onChangeOrder={onChangeOrder} />

      <div className={styles.filterBy}>
        <div className={styles.title} {...skeletonTextSelector}>
          Filter by
        </div>

        <Accordion>{filters}</Accordion>
      </div>

      <ShowDealsButton
        onClick={toggleSidebar}
        isOpen={isSidebarOpen}
        isLoading={loading}
        availableDealsCount={availableDealsCount}
      />
    </div>
  )
}

export default Sidebar
